import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import React, {useEffect, useRef, useState} from "react";
import {DeleteItemsApi} from "../../../../../../../reduxToolkit/Commons/CommonsApi";
import {
    DEFAULT_LATITUDE, DEFAULT_LONGITUDE,
    errorMsg,
    filterByName, tryGetAddress,
} from "../../../../../../../Apis/functions";
import {ATTENDANCE_SUBVIEW_MODEL, TABS_ATTENDANCE} from "../../../Model/Model";
import DropDownMenuProfile from "../../../../../../../components/DropDown/DropDownMenuProfile";
import {permissionObj} from "../../../../../../../Helpers/permission";
import DropDownMenuItem from "../../../../../../../components/DropDown/DropDownMenuItem";
import routes from "../../../../../../../Routes/routes";
import {
    DeleteIconsOptions,
    DetailsIconsOptions,
    IconOptions, UpdateIconsOptions,
} from "../../../../../../../components/buttons/IconButtons";
import TableFunction from "../../../../../../../components/table/TableFunction";
import {ATTENDANCE_GEOFENCES_MODEL} from "../Model/Model";
import {debounce} from "lodash";
import {STATUS_MODEL} from "../../../../../../../enums/statusEnum";
import {toast} from "react-toastify";
import {TABLES} from "../../../../../../../Apis/Tables";
import {useTranslation} from "react-i18next";
import {
    GetGeofenceList, UpdateGeofenceHierarchyLevel,
    UpdateGeofenceStatus
} from "../../../../../../../reduxToolkit/AttendanceGeofence/AttendanceGeofenceApi";
import {resetGetGeofenceList} from "../../../../../../../reduxToolkit/AttendanceGeofence/AttendanceGeofenceSlice";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 * Created At: 21 de 01 de 2026
 */
export const GeofenceApp = ({
                                searchElement,
                                setSearchElement
                            }
) => {
    const { t } = useTranslation();
    const elementRef = useRef(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [geofences, setGeofences] = useState([]);
    const [dayArray, setDayArray] = useState([]);
    const [direction, setDirection] = useState();
    const [selected, setSelected] = useState(null);
    const [center, setCenter] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [modified, setModified] = useState(false);
    const [deleteModalShow, setDeleteModalShow] = useState(false);
    const [showActiveInactiveModal, setShowActiveInactiveModal] = useState(false);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const { openMenu, handleMenuToggle, handleClose } = TableFunction({});
    const { getGeofenceList } = useSelector((state) => state?.AttendanceGeofenceSlice);

    // Observe container height changes
    useEffect(() => {
        if (!elementRef.current) return;

        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                const height = entry.contentRect.height;
                entry.target.style.setProperty('--element-height', `${height}px`);
                entry.target.style.setProperty('height', `${height}px`);
            }
        });

        observer.observe(elementRef.current);
        return () => observer.disconnect();
    }, []);

    // Fetch geofence data on mount
    useEffect(() => {
        getData();
    }, []);

    // Filter geofences whenever searchElement or API list changes
    useEffect(() => {
        if (getGeofenceList.length === 0) return;
        if (searchElement === "") {
            setGeofences(ATTENDANCE_GEOFENCES_MODEL.buildGeofenceListMap(getGeofenceList, setDayArray));
        } else {
            const filteredOptions = filterByName(geofences, searchElement);
            setGeofences(filteredOptions);
        }
    }, [searchElement, getGeofenceList]);

    // Refetch data after modifications
    useEffect(() => {
        if (modified) {
            getData();
            setModified(false);
        }
    }, [modified]);

    /**
     * Fetches geofences from API and updates state.
     */
    const getData = () => {
        dispatch(resetGetGeofenceList());
        setSearchElement("");
        dispatch(GetGeofenceList()).then(({ payload: { data: { data, success } } }) => {
            if (success) {
                if(data.length > 0){
                    let aux = ATTENDANCE_GEOFENCES_MODEL.buildGeofenceListMap(data);
                    setGeofences(aux);
                    let center = ATTENDANCE_GEOFENCES_MODEL.getGeofenceCenter(aux[0]);
                    if(center) {
                        setCenter(center);
                        handleMapMove(center[0], center[1])
                    }
                }else{
                    handleMapMove(DEFAULT_LATITUDE, DEFAULT_LONGITUDE)
                }
            }
        });
    };

    const handleMapMove = debounce((lat, lng) => {
        tryGetAddress(lat, lng, dispatch, setDirection);
    }, 800);

    /**
     * Returns JSX for dropdown action buttons for each geofence
     */
    const getActionsButtons = ({ geofence, backgroundColor }) => (
        <div className={`d-flex align-items-center ${backgroundColor}`}>
            <DropDownMenuProfile
                menuId={`menu-${geofence?.geofenceId}`}
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                iconOptions={<IconOptions className={'icon-color-shades-400'} />}
            >
                {(DEV || permission?.includes(permissionObj?.WEB_ATTENDANCE_MANAGE_GEOFENCES_UPDATE)) ?
                    <DropDownMenuItem
                        handleClose={handleClose}
                        name={t('update')}
                        icon={<UpdateIconsOptions />}
                        click={() => goUrl(`${routes.attendanceGeofence.update}${geofence?.geofenceId}`)}
                    />
                    :
                    <DropDownMenuItem
                        handleClose={handleClose}
                        name={t('details')}
                        icon={<DetailsIconsOptions />}
                        click={() => goUrl(`${routes.attendanceGeofence.details}${geofence?.geofenceId}`)}
                    />
                }

                {(DEV || permission?.includes(permissionObj?.WEB_ATTENDANCE_MANAGE_GEOFENCES_DELETE)) &&
                    <DropDownMenuItem
                        handleClose={handleClose}
                        name={t('delete')}
                        fontColor='font-color-error-600'
                        iconColor={"icon-color-error-code-600"}
                        icon={<DeleteIconsOptions />}
                        click={() => {
                            setDeleteModalShow(true);
                            setSelected(geofence);
                        }}
                    />
                }

            </DropDownMenuProfile>
        </div>
    );

    /**
     * Navigate to a specific URL while preserving tab and subview state.
     */
    const goUrl = (url) =>{
        navigate(url, {
            state: {
                currentTab: TABS_ATTENDANCE.MANAGE.INDEX,
                currentSubView: ATTENDANCE_SUBVIEW_MODEL.MANAGE.GEOFENCE},
        });
    }
    /**
     * Toggle geofence status (active/inactive)
     */
    const handleUpdateStatus = () => {
        const statusId = selected.statusId === STATUS_MODEL.ACTIVE_COMPANY
            ? STATUS_MODEL.IN_ACTIVE_COMPANY
            : STATUS_MODEL.ACTIVE_COMPANY;

        dispatch(UpdateGeofenceStatus({ id: selected.geofenceId, statusId })).then(
            ({ payload: { data: { success, code } } }) => {
                if (success) {
                    toast.success(t("has_been_updated_successfully"));
                    setModified(true);
                } else errorMsg(code);
            }
        );
    };

    /**
     * Updates hierarchy level of a geofence
     */
    const handleUpdateHierarchyLevel = (data) => {
        dispatch(UpdateGeofenceHierarchyLevel({ id: data.id, hierarchyLevel: parseInt(data.newIndex + 1) }))
            .then(({ payload: { data: { success, code } } }) => {
                if (success) {
                    toast.success(t("has_been_updated_successfully"));
                    getData();
                } else errorMsg(code);
            });
    };

    /**
     * Deletes the selected geofence
     */
    const handleDeleteSelected = () => {
        if (selected?.id) {
            dispatch(DeleteItemsApi({ tableName: TABLES.ATTENDANCE_MANAGE_GEOFENCES, body: [selected?.geofenceId] }))
                .then(({ payload: { data: { success, code } } }) => {
                    if (success) {
                        setModified(true);
                        setSelected(null);
                    } else {
                        errorMsg(code);
                    }
                });
        }
    };

    return {
        t,
        DEV,
        goUrl,
        center,
        dayArray,
        expanded,
        selected,
        geofences,
        direction,
        permission,
        setSelected,
        setExpanded,
        setGeofences,
        handleMapMove,
        deleteModalShow,
        getActionsButtons,
        handleUpdateStatus,
        setDeleteModalShow,
        handleDeleteSelected,
        showActiveInactiveModal,
        setShowActiveInactiveModal,
        handleUpdateHierarchyLevel,
    }
}
